/* Geral / Bibliotecas */
import React, { useCallback, useEffect, useMemo, useRef } from "react";
import { connect } from "react-redux";
import PropTypes from "prop-types";
import { withRouter } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import ReactTable from "react-table";
import withReactContent from "sweetalert2-react-content";
import Swal from "sweetalert2";
import "react-table/react-table.css";

/* Métodos / Ações */
import { clearFuncionarioId } from "../../actions/scroll";
import {
    addExplicacaoDocumentoEmpresa,
  deleteDocumentoEmpresa,
  fetchDocumentosEmpresaList,
} from "../../actions/empresas";

/* Componentes */
import Error from "../Error";
import LetterCell from "../Table/Cells/LetterCell";
import DocumentExplanationModal from "./DocumentExplanationModal";

/* Hooks */
import { useLoading } from "../../hooks/useLoading";
import { usePaginatedData } from "../../hooks/usePaginatedData";

const MySwal = withReactContent(Swal);

const DocumentosEmpresa = ({ dispatch, error, modal }) => {
  const loading = useLoading();
  const { rows, pages, setRows, setPages, reset } = usePaginatedData();

  const ref = useRef(null);

  const requestData = useCallback(
    async (state) => {
      const reqData = {
        pageSize: state?.pageSize ?? null,
        page: state?.page ?? null,
        search: state?.filtered?.[0]?.value || null,
      };

      loading.start();

      try {
        const res = await fetchDocumentosEmpresaList(reqData, dispatch);
        setRows(res.rows);
        setPages(res.pages);
      } catch {
        reset();
      } finally {
        loading.stop();
      }
    },
    [setRows, setPages, dispatch, reset, loading],
  );

  const handleEdit = useCallback((props) => {
    MySwal.fire({
      title: "",
      type: "info",
      html: (
        <DocumentExplanationModal
          documento={props.original}
          onSubmit={async (payload) => {
            const values = {
              documento: props.original.documento,
              explicacao: payload.explicacao,
            };
            loading.start();
            MySwal.clickConfirm();

            await addExplicacaoDocumentoEmpresa(
              values,
              () => ref.current.fireFetchData(),
              () => loading.stop(),
            );
          }}
          onCancel={MySwal.clickCancel}
        />
      ),
      showCancelButton: false,
      showConfirmButton: false,
    });
  }, [loading]);

  const handleDelete = useCallback(
    (props) => {
      MySwal.fire({
        title: "",
        type: "warning",
        html: `Deseja realmente excluir o documento<br><b>${props.original.nome}</b>?`,
        showCancelButton: true,
        confirmButtonText: "Sim, excluir",
        cancelButtonText: "Cancelar",
        reverseButtons: true,
      }).then((result) => {
        if (result.value) {
          loading.start();
          dispatch(
            deleteDocumentoEmpresa(
              props.original.documento,
              () => ref.current.fireFetchData(),
              () => loading.stop(),
            ),
          );
        }
      });
    },
    [dispatch, loading],
  );

  const fetchData = useCallback((state) => requestData(state), [requestData]);

  const columns = useMemo(
    () => [
      {
        Header: "",
        accessor: "letra",
        filterable: false,
        sortable: false,
        className: "option-column-5 letra-inicial",
        headerClassName: "option-column-5",
        Cell: (props) => <LetterCell {...props} />,
      },
      {
        Header: "Nome",
        accessor: "nome",
        sortable: false,
        headerClassName: "nome-placeholder",
        className: "font-weight-600 text-uppercase",
      },
      {
        Header: "",
        filterable: false,
        sortable: false,
        className: "option-column-5",
        headerClassName: "option-column-5",
        Cell: (props) => (
          <button className="a" onClick={() => handleEdit(props)} title="Editar">
            <FontAwesomeIcon icon="pencil-alt" />
          </button>
        ),
      },
      {
        Header: "",
        filterable: false,
        sortable: false,
        className: "option-column-5",
        headerClassName: "option-column-5",
        Cell: (props) =>
          props.original.ativo ? (
            <button
              className="a"
              onClick={() => handleDelete(props)}
              title="Apagar"
            >
              <FontAwesomeIcon icon="trash" />
            </button>
          ) : null,
      },
    ],
    [handleDelete, handleEdit],
  );

  useEffect(() => {
    dispatch(clearFuncionarioId());
  }, [dispatch]);

  return error && modal === "" ? (
    <Error erro={error}></Error>
  ) : (
    <div className="pb-3">
      <div className="row justify-content-center mx-0">
        <div className="col-12 px-0">
          <div className="card tertium-card">
            <div className="card-header py-3 px-2 px-lg-5">
              Documentos Empresa
            </div>
            <div className="card-body px-2 px-lg-5">
              <ReactTable
                ref={ref}
                defaultSorted={[
                  {
                    id: "nome",
                    desc: false,
                  },
                ]}
                columns={columns}
                manual
                multiSort={false}
                data={rows}
                pages={pages}
                loading={loading.value}
                onFetchData={fetchData}
                filterable
                previousText={<FontAwesomeIcon icon="chevron-left" />}
                nextText={<FontAwesomeIcon icon="chevron-right" />}
                resizable={false}
                loadingText="Carregando..."
                noDataText="Nenhum documento encontrado"
                pageText="Página"
                ofText="de"
                rowsText="linhas"
                pageJumpText="ir para a página"
                rowsSelectorText="linhas por página"
                pageSizeOptions={[5, 10, 20, 50]}
                defaultPageSize={10}
                minRows={0}
                className="-highlight"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

DocumentosEmpresa.propTypes = {
  modal: PropTypes.string.isRequired,
};

const mapStateToProps = (state) => ({
  modal: state.modal.modalOpen,
  error: state.errors.message,
});

export default connect(mapStateToProps)(withRouter(DocumentosEmpresa));
